'use client';

import { useMemo } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { CMGroup, CMOption } from '@quick/components/ComposableMenu';

import { useAppTranslation } from '@/app/i18n/client';
import Balance from '@/assets/menuIcons/balance.svg';
import Services from '@/assets/menuIcons/briefcase.svg';
import Dashboard from '@/assets/menuIcons/chart-line.svg';
import Chat from '@/assets/menuIcons/chat.svg';
import Clients from '@/assets/menuIcons/clients.svg';
import Config from '@/assets/menuIcons/configuration.svg';
import Logs from '@/assets/menuIcons/logs.svg';
import Messages from '@/assets/menuIcons/messages.svg';
import Payments from '@/assets/menuIcons/payments.svg';
import Places from '@/assets/menuIcons/places.svg';
import Providers from '@/assets/menuIcons/provider.svg';
import Quicker from '@/assets/menuIcons/quicker.svg';
import Quotation from '@/assets/menuIcons/quotation.svg';
import Rates from '@/assets/menuIcons/rates.svg';
import Reports from '@/assets/menuIcons/reports.svg';
import Massive from '@/assets/menuIcons/upload.svg';
import Users from '@/assets/menuIcons/users.svg';
import Workers from '@/assets/menuIcons/workers.svg';
import { useLangProvider } from '@/components/core/AppLayout/AppLangProvider';
import {
  NotificationIcon,
  NotificationInteractable,
} from '@/components/core/OpPlLayout/Notifications';
import { envs } from '@/layers/core/data/const';
import { useCanReadWrite } from '@/layers/core/deal/services/useCanReadWrite';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { useUser } from '@/layers/core/deal/services/useUser';
import { useUserInfo } from '@/layers/core/deal/services/useUserInfo';

export default function MenuOptions() {
  const { t } = useAppTranslation(['OpPl', 'common']);
  const lang = useLangProvider();
  const pathname = usePathname();
  const { id } = useUser();
  // useNotifier();

  const { data: userData } = useUserInfo();
  const p = userData?.module_permissions;

  // Logic to hide payments module
  const { r: canReadPayments } = useCanReadWrite(
    p?.platform?.payments?.payment
  );
  const { data: paymentMethods } = useFetch<{ id: number }[]>(
    canReadPayments && {
      url: '/services/payment-method/users',
      auth: true,
      params: { user: id },
    }
  );
  const hasBagOrCreditCard = useMemo(
    () =>
      !!paymentMethods?.find(({ id }) => ['2', '3'].includes(id.toString())),
    [paymentMethods]
  );

  // PL menu options
  const showQuicker = p?.platform?.pwa || !id;
  const showDashboard = p?.platform?.dashboards;
  const showServices = p?.platform?.services;
  const showQuotations = p?.platform?.quotations;
  const showAddresses = p?.platform?.address;
  const showReports = p?.platform?.reports;
  const showPayments = p?.platform?.payments && hasBagOrCreditCard;
  const canReadBulkLoad = p?.platform?.bulkLoads;
  const canReadCompany = p?.platform?.company;
  const hasPl =
    !!showQuicker ||
    !!showDashboard ||
    !!showServices ||
    !!showQuotations ||
    !!showAddresses ||
    !!showReports ||
    !!showPayments ||
    !!canReadBulkLoad ||
    !!canReadCompany;

  // OP menu options
  const showOpDashboard = !!p?.operations?.dashboard;
  const showOpServices = !!p?.operations?.services;
  const showOpQuotations = !!p?.operations?.quotation;
  const showOpWorkers = !!p?.operations?.workers;
  const showOpClients = !!p?.operations?.clients;
  const showOpProviders = !!p?.operations?.providers;
  const showOpUsers = !!p?.operations?.users;
  const showOpReports = !!p?.operations?.reports;
  const showOpBulkLoad = !!p?.operations?.clients?.bulkLoad;
  const showOpMessages = !!p?.operations?.messages;
  const showOpChat = !!p?.operations?.chats && !!envs.chatToken && !!envs.chat;
  const showOpRates = !!p?.operations?.rates;
  const showOpBalance = !!p?.operations?.balances;

  const showOpLogs = !!p?.operations?.logs;
  const logsDefaultTab = p?.operations?.logs?.log
    ? 'logs'
    : p?.operations?.logs?.homologator
      ? 'homologator'
      : 'webhooks';

  const showOpConfig = !!p?.operations?.configuration;
  const hasOp =
    showOpDashboard ||
    showOpServices ||
    showOpQuotations ||
    showOpWorkers ||
    showOpClients ||
    showOpProviders ||
    showOpUsers ||
    showOpReports ||
    showOpMessages ||
    showOpChat ||
    showOpRates ||
    showOpBalance ||
    showOpLogs ||
    showOpConfig;

  const isOpPl = hasOp && hasPl;

  const pl = (
    <CMGroup title={t('OpPl:menu.platform')} group={isOpPl}>
      {showQuicker && (
        <CMOption
          Icon={Quicker}
          text={t('OpPl:menu.pl.quicker')}
          render={(p) => <Link href={`/${lang}`} {...p} />}
          active={pathname === `/${lang}`}
        />
      )}
      {showDashboard && (
        <CMOption
          Icon={Dashboard}
          text={t('OpPl:menu.op.dashboard')}
          render={(p) => <Link href={`/${lang}/platform/dashboard`} {...p} />}
          active={pathname === `/${lang}/platform/dashboard`}
        />
      )}
      {showServices && (
        <CMOption
          Icon={Services}
          text={t('OpPl:menu.pl.services')}
          render={(p) => <Link href={`/${lang}/platform/services`} {...p} />}
          active={pathname === `/${lang}/platform/services`}
        />
      )}
      {showQuotations && (
        <CMOption
          Icon={Quotation}
          text={t('OpPl:menu.op.quotation')}
          render={(p) => <Link href={`/${lang}/platform/quotations`} {...p} />}
          active={pathname === `/${lang}/platform/quotations`}
        />
      )}
      {showAddresses && (
        <CMOption
          Icon={Places}
          text={t('OpPl:menu.pl.addresses')}
          render={(p) => <Link href={`/${lang}/platform/addresses`} {...p} />}
          active={pathname === `/${lang}/platform/addresses`}
        />
      )}
      {showReports && (
        <CMOption
          Icon={Reports}
          text={t('OpPl:menu.pl.reports')}
          render={(p) => <Link href={`/${lang}/platform/reports`} {...p} />}
          active={pathname === `/${lang}/platform/reports`}
        />
      )}
      {showPayments && (
        <CMOption
          Icon={Payments}
          text={t('OpPl:menu.pl.payments')}
          render={(p) => <Link href={`/${lang}/platform/payments`} {...p} />}
          active={pathname === `/${lang}/platform/payments`}
        />
      )}
      {canReadBulkLoad && (
        <CMOption
          Icon={Massive}
          text={t('OpPl:menu.pl.massiveLoad')}
          render={(p) => <Link href={`/${lang}/platform/massive`} {...p} />}
          active={pathname === `/${lang}/platform/massive`}
        />
      )}
      {canReadCompany && (
        <CMOption
          Icon={Clients}
          text={t('OpPl:menu.pl.bm')}
          render={(p) => <Link href={`/${lang}/platform/bm`} {...p} />}
          active={pathname === `/${lang}/platform/bm`}
        />
      )}
    </CMGroup>
  );

  const op = (
    <CMGroup title={t('OpPl:menu.operations')} group={isOpPl}>
      {showOpDashboard && (
        <CMOption
          Icon={Dashboard}
          text={t('OpPl:menu.op.dashboard')}
          render={(p) => <Link href={`/${lang}/operations/dashboard`} {...p} />}
          active={pathname === `/${lang}/operations/dashboard`}
        />
      )}
      {showOpServices && (
        <CMOption
          Icon={Services}
          text={t('OpPl:menu.op.services')}
          render={(p) => <Link href={`/${lang}/operations/services`} {...p} />}
          active={pathname === `/${lang}/operations/services`}
        />
      )}
      {showOpQuotations && (
        <CMOption
          Icon={Quotation}
          text={t('OpPl:menu.op.quotation')}
          render={(p) => (
            <Link href={`/${lang}/operations/quotations`} {...p} />
          )}
          active={pathname === `/${lang}/operations/quotations`}
        />
      )}
      {showOpWorkers && (
        <CMOption
          Icon={Workers}
          text={t('OpPl:menu.op.workers')}
          render={(p) => <Link href={`/${lang}/operations/workers`} {...p} />}
          active={pathname?.startsWith(`/${lang}/operations/workers`)}
        />
      )}
      {showOpClients && (
        <CMOption
          Icon={Clients}
          text={t('OpPl:menu.op.clients')}
          render={(p) => <Link href={`/${lang}/operations/clients`} {...p} />}
          active={pathname === `/${lang}/operations/clients`}
        />
      )}
      {showOpProviders && (
        <CMOption
          Icon={Providers}
          text={t('OpPl:menu.op.providers')}
          render={(p) => <Link href={`/${lang}/operations/providers`} {...p} />}
          active={pathname === `/${lang}/operations/providers`}
        />
      )}
      {showOpUsers && (
        <CMOption
          Icon={Users}
          text={t('OpPl:menu.op.users')}
          render={(p) => <Link href={`/${lang}/operations/users`} {...p} />}
          active={pathname === `/${lang}/operations/users`}
        />
      )}
      {showOpReports && (
        <CMOption
          Icon={Reports}
          text={t('OpPl:menu.op.reports')}
          render={(p) => <Link href={`/${lang}/operations/reports`} {...p} />}
          active={pathname === `/${lang}/operations/reports`}
        />
      )}
      {showOpBulkLoad && (
        <CMOption
          Icon={Massive}
          text={t('OpPl:menu.pl.massiveLoad')}
          render={(p) => <Link href={`/${lang}/operations/massive`} {...p} />}
          active={pathname?.includes(`/${lang}/operations/massive`)}
        />
      )}
      {showOpMessages && (
        <CMOption
          Icon={Messages}
          text={t('OpPl:menu.op.messages')}
          render={(p) => <Link href={`/${lang}/operations/messages`} {...p} />}
          active={pathname === `/${lang}/operations/messages`}
        />
      )}
      {showOpChat && (
        <CMOption
          Icon={Chat}
          text="Chat"
          render={(p) => <Link href={`/${lang}/operations/chat`} {...p} />}
          active={pathname === `/${lang}/operations/chat`}
        />
      )}
      {showOpRates && (
        <CMOption
          Icon={Rates}
          text={t('OpPl:menu.op.rates')}
          render={(p) => (
            <Link href={`/${lang}/operations/rates/routes`} {...p} />
          )}
          active={pathname?.startsWith(`/${lang}/operations/rates`)}
        />
      )}
      {showOpBalance && (
        <CMOption
          Icon={Balance}
          text={t('OpPl:menu.op.balance')}
          render={(props) => (
            <Link
              href={`/${lang}/operations/balance/${p.operations?.balances?.balance ? 'pay_bill' : 'advances'}`}
              {...props}
            />
          )}
          active={pathname?.startsWith(`/${lang}/operations/balance`)}
        />
      )}
      {showOpLogs && (
        <CMOption
          Icon={Logs}
          text={t('OpPl:menu.op.logs')}
          render={(p) => (
            <Link href={`/${lang}/operations/logs/${logsDefaultTab}`} {...p} />
          )}
          active={pathname?.startsWith(`/${lang}/operations/logs`)}
        />
      )}
      {showOpConfig && (
        <CMOption
          Icon={Config}
          text={t('OpPl:menu.op.config')}
          render={(p) => <Link href={`/${lang}/operations/config`} {...p} />}
          active={pathname?.startsWith(`/${lang}/operations/config`)}
        />
      )}
    </CMGroup>
  );

  const options = (
    <>
      {hasOp && (
        <CMOption
          Icon={NotificationIcon}
          text={t('OpPl:menu.notifications')}
          render={(p) => <NotificationInteractable {...p} />}
        />
      )}
      {pl}
      {op}
    </>
  );
  return options;
}
